import React, { useEffect, useMemo, useState } from 'react';
import { getOptionsChain, getOptionsChecklist, getOptionsVolatility } from '../api/options';
import './DirectionalOptionsReader.css';

const n = value => Number.isFinite(Number(value)) ? Number(value) : null;
const pct = value => n(value) == null ? '—' : `${n(value).toFixed(1).replace('.', ',')}%`;
const money = value => n(value) == null ? '—' : `R$ ${n(value).toFixed(2).replace('.', ',')}`;
const date = value => value ? String(value).slice(0, 10).split('-').reverse().join('/') : '—';
const verdictOf = verdict => typeof verdict === 'object' ? verdict?.ativo : verdict;
const verdictTone = verdict => ['verde', 'amarelo', 'PASS'].includes(String(verdict || '')) ? 'ok' : 'warn';
const itemIcon = status => ({ ok: '✓', warn: '!', fail: '✕' }[status] || '!');
const availabilityMessage = availability => ({
  indisponível_plano: 'A cadeia está indisponível no plano atual. Isso não significa que o ativo não tenha opções; consulte uma fonte/plano habilitado.',
  sem_cadeia_hoje: 'A fonte foi consultada hoje, mas nenhum contrato elegível foi encontrado. Isso não altera o cadastro permanente do ativo.',
  não_atualizado: 'Ainda não há uma atualização de cadeia para este ativo.',
}[availability]);

function Signal({ label, value, tone }) {
  return <span className={`dor-signal ${tone || ''}`}><i />{label}<b>{value}</b></span>;
}

export default function DirectionalOptionsReader({ ticker, setup, mode = 'call', expanded = false, onToggle, onEntryViaChange, showEntryVia = setup !== 'breakout55' }) {
  const [state, setState] = useState({ loading: false, error: null, volatility: null, chain: null, checklist: null });
  const [entryVia, setEntryVia] = useState('ativo');
  const callVerdict = verdictOf(state.checklist?.verdict);
  const contracts = useMemo(() => state.chain?.contracts || [], [state.chain]);
  const callUnavailable = state.chain?.availability !== 'disponível' || contracts.length === 0
    || !['verde', 'amarelo', 'PASS'].includes(String(state.checklist?.verdict?.call || ''));
  useEffect(() => {
    setEntryVia('ativo');
    onEntryViaChange?.('ativo');
  }, [ticker]);
  useEffect(() => {
    if (!expanded || !ticker) return;
    let active = true;
    setState(current => ({ ...current, loading: true, error: null }));
    Promise.allSettled([
      getOptionsVolatility(ticker),
      getOptionsChain(ticker, setup === 'reversal' ? 'call_direcional_reversao' : 'call_direcional_rompimento'),
      getOptionsChecklist(ticker, setup, mode),
    ]).then(([volatility, chain, checklist]) => {
      if (!active) return;
      const checklistValue = checklist.status === 'fulfilled' ? checklist.value : null;
      const error = checklistValue ? null : (checklist.reason?.response?.data?.error || checklist.reason?.message || 'Checklist indisponível.');
      setState({
        loading: false,
        error,
        volatility: volatility.status === 'fulfilled' ? volatility.value : null,
        chain: chain.status === 'fulfilled' ? chain.value : { contracts: [], availability: 'não_atualizado' },
        checklist: checklistValue,
      });
    });
    return () => { active = false; };
  }, [expanded, ticker, setup, mode]);

  return (
    <section className="dor" data-testid={`options-reader-${ticker || 'empty'}`}>
      <button type="button" className="dor-toggle" onClick={onToggle} data-testid={`button-expand-options-${ticker || 'empty'}`}>
        <span><small>OPÇÕES DIRECIONAIS</small><strong>{ticker || 'Selecione um ativo'}</strong></span>
        <b>{expanded ? 'Recolher' : 'Ler cadeia e checklist'}</b>
      </button>
      {expanded && <div className="dor-body">
        {state.loading && <div className="dor-loading"><span /><span /><span /></div>}
        {state.error && <div className="dor-error" role="alert">{state.error}</div>}
         {!state.loading && !state.error && state.checklist && <>
          <div className="dor-signals">
            <Signal label="Ativo" value={callVerdict || '—'} tone={verdictTone(callVerdict)} />
            <Signal label="Call" value={state.checklist?.verdict?.call || state.chain?.availability || '—'} tone={callUnavailable ? 'muted' : 'ok'} />
             <Signal label="IV Rank" value={pct(state.volatility?.iv_display)} tone="info" />
             <Signal label="IV/HV" value={n(state.volatility?.iv_hv_ratio) == null ? '—' : n(state.volatility?.iv_hv_ratio).toFixed(2)} tone="info" />
          </div>
           <div className="dor-meta">HV20 {pct(state.volatility?.hv20)} · HV60 {pct(state.volatility?.hv60)} · {state.volatility?.label || state.volatility?.source || 'volatilidade ainda não disponível'}</div>
          {showEntryVia && <div className="dor-entry">
            <span>Inclusão direcional via</span>
            <label><input type="radio" name={`entry-via-${ticker}`} value="ativo" checked={entryVia === 'ativo'} onChange={e => { setEntryVia(e.target.value); onEntryViaChange?.(e.target.value); }} /> Ativo</label>
            <label title={callUnavailable ? 'Call indisponível para esta leitura.' : undefined}><input type="radio" name={`entry-via-${ticker}`} value="call" checked={entryVia === 'call'} disabled={callUnavailable} onChange={e => { setEntryVia(e.target.value); onEntryViaChange?.(e.target.value); }} /> Call</label>
          </div>}
          <div className="dor-contracts">
             {ticker && <button type="button" className="dor-grid-link" onClick={() => { window.location.href = `/opcoes/${encodeURIComponent(ticker)}?strategy=${encodeURIComponent(setup === 'reversal' ? 'call_reversal' : 'call_breakout')}`; }}>Abrir grade de opções</button>}
            {contracts.slice(0, 3).map(contract => <div className="dor-contract" key={contract.optionSymbol || `${contract.strike}-${contract.expiration}`}>
              <strong>{contract.optionSymbol || 'Contrato sem símbolo'}</strong>
              <span>Strike {money(contract.strike)} · Venc. {date(contract.expiration)}</span>
              <span>Delta {n(contract.delta) == null ? '—' : n(contract.delta).toFixed(2)} · Mid {money(contract.mid)}</span>
              <span>Breakeven {money(contract.breakeven)} · Movimento {pct(contract.required_move_pct ?? contract.expected_move_pct)}</span>
            </div>)}
             {!contracts.length && <div className="dor-empty">{state.chain?.availabilityReason || availabilityMessage(state.chain?.availability) || 'Nenhum contrato retornado nesta atualização.'}</div>}
          </div>
           <div className="dor-checklist">
             <strong>Checklist: ativo {state.checklist?.verdict?.ativo || '—'} · call {state.checklist?.verdict?.call || '—'}</strong>
             <span>{state.checklist?.message || 'Leitura educativa sem decisão automática.'}</span>
             <ul>{(state.checklist?.items || []).map(item => <li key={item.id} className={item.status}><b>{itemIcon(item.status)}</b><span>{item.label}</span><small>{item.detail}</small></li>)}</ul>
           </div>
        </>}
      </div>}
    </section>
  );
}